import { createEffect, untrack } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { sessionActor } from "@/lib/session-machine";
import { useQuery } from "@tanstack/solid-query";
import { queryKeys } from "@tendly/shared";
import { cloneSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";

interface AppError {
  message?: string;
  status?: number;
}

function isAppError(error: unknown): error is AppError {
  return typeof error === "object" && error !== null && ("message" in error || "status" in error);
}

/**
 * Singleton component that synchronizes the TanStack Query session state
 * with the XState session actor.
 *
 * This MUST only be rendered once in the app (usually in AppProviders)
 * to prevent infinite reactivity loops.
 */
export function SessionBridge() {
  const snapshot = fromActorRef(sessionActor);

  const query = useQuery(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    // Only fetch if the machine is done priming (local storage check)
    enabled: !untrack(() => snapshot().value === "priming"),
    staleTime: SESSION_STALE_TIME_MS,
  }));

  createEffect(() => {
    // 1. Only sync if the query has finished its first load
    if (!query.isPending) {
      // P23/CC-60 · I1: `data` is the only proxy field that SUSPENDS, and this
      // component suspends directly from the `<Suspense fallback={null}>` in
      // `__root` (it is mounted in `AppProviders`, above the `<RouterProvider>`).
      // `!isPending` is NOT enough: on error it is also `false` with no `data` value.
      //
      // And merely saving the READ is not enough: in `main`, `.data` in the ERROR
      // state aborted this effect, so a 401 never cleared the session primed from
      // `localStorage`. Saving only the read would keep the effect alive and send
      // `SERVER_REPLY(session: null)` → the user's account would disappear every time
      // the server did not answer. The early return is DELIBERATE: it reproduces the
      // previous abort. `runSessionCheck` from `beforeLoad`, not this bridge, confirms
      // guest mode.
      if (!query.isSuccess) {
        return;
      }
      const sessionData = query.data ? cloneSession(query.data) : null;

      // 2. Prevent unnecessary updates using robust manual comparison.
      // Use untrack to avoid dependency on the machine state while reading it.
      const current = untrack(() => snapshot().context.session);
      const currentErr = untrack(() => {
        const err = snapshot().context.error;
        return isAppError(err) ? err : null;
      });

      const isDataSame =
        (!sessionData && !current) || (sessionData && sessionData.user.id === current?.user.id);

      // After the `isSuccess` guard above, the error is ALWAYS `null` (the error
      // path no longer reaches here), so the comparison reduces to
      // “was there an error in the machine that this success should clear?”.
      const isErrorSame = !currentErr;

      if (isDataSame && isErrorSame) {
        return;
      }

      console.log("[SessionBridge] Syncing session to machine:", {
        hasData: Boolean(sessionData),
        hasError: false,
      });

      sessionActor.send({
        type: "SERVER_REPLY",
        session: sessionData,
        error: null,
      });
    }
  });

  return null;
}
